import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { storePlantRowData } from "../../Store/TemplateStore/template.action";
import { useTranslation } from "react-i18next";

import {
  getUsers,
  getPlants,
  sharePlant,
  unsharePlant,
} from "../../network/cipAPI";
import DeleteRenderer from "../../components/AddCIPAndShutdown/deleteCellRender";
import DupontButton from "../../components/DupontButton";
import DupontDialog from "../../components/DupontDialog";
import AgTable from "../../components/AgTable";
import InputBox from "../../components/InputBox";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { getIndex } from "../../utils/Utils";

export const CustomMembraneCellRenderer = ({
  data: { sharing, is_owner, plant_id },
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState("");
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);

  const userEmail = useSelector((state) => state.userEmail);
  const dispatch = useDispatch();
  const handleConfirm = async () => {
    setShowOverlayLoader(true);
    try {
      const payload = { shared_email: email, comments };

      const response = await sharePlant(plant_id, payload);
      if (response && response.message) {
        setDialogOpen(false);
        createNotification(SUCCESS_NOTIFICATION, response.message, "");
      } else {
        createNotification(ERROR_NOTIFICATION, response.detail.error, "");
        setErrorMsg(response.detail.error);
      }
    } catch (error) {
      console.error("Error during sharing plant:", error);
    } finally {
      setShowOverlayLoader(false);
      getPlantDetails();
    }
  };

  const getPlantDetails = async () => {
    try {
      const response = await getPlants();
      if (response && response.data) {
        dispatch(storePlantRowData(response.data));
      } else {
        dispatch(storePlantRowData([]));
      }
    } catch (err) {
    } finally {
    }
  };

  const getUserInfo = async () => {
    setShowOverlayLoader(true);
    try {
      const response = await getUsers(plant_id);
      if (response) {
        setRowData(response.data);
        setShowOverlayLoader(false);
      }
    } catch (error) {
      console.error("Error during getting user details:", error);
    } finally {
      setShowOverlayLoader(false);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setErrorMsg(null);
    getPlantDetails();
  };
  const onClose = (action) => {
    if (action === "CONFIRM") {
      handleConfirm(email);
    } else if (action === "CANCEL") {
      handleCancel();
    }
  };

  const handleUnshare = async (email) => {
    setShowOverlayLoader(true);
    try {
      const response = await unsharePlant(plant_id, email);
      if (response && response.message) {
        setRowData((prev) => prev.filter((user) => user.email !== email));
        createNotification(SUCCESS_NOTIFICATION, response.message, "");
        getUserInfo();
      } else {
        createNotification(ERROR_NOTIFICATION, response.detail.error, "");
      }
    } catch (error) {
      console.error("Error during sharing plant:", error);
    } finally {
      setShowOverlayLoader(false);
    }
  };
  const deleteHandler = async (params) => {
    handleUnshare(params.email);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
    getUserInfo();
  };

  const handleCommentsChange = (value) => {
    setComments(value);
  };

  const handleChange = (value) => {
    setEmail(value);
    if (value.trim() === "") {
      setErrorMsg(t("sharePlant.emptyErrorMessage"));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setErrorMsg([t("sharePlant.invalidEmail")]);
    } else {
      setErrorMsg("");
    }
  };

  const colDefs = [
    {
      headerName: t("manageAccess.index"),
      valueGetter: (params) => getIndex(params, params.data.email),
      minWidth: 5,
    },
    {
      headerName: t("manageAccess.email"),
      field: "email",
      minWidth: 200,
    },
    {
      headerName: t("manageAccess.type"),
      field: "type",
      minWidth: 50,
    },
    {
      headerName: t("manageAccess.comments"),
      field: "comments",
      minWidth: 200,
    },
    {
      colId: "delete",
      lockPinned: true,
      pinned: "right",
      width: 50,
      headerName: t("ValueStrings.delete"),
      cellRenderer: DeleteRenderer,
    },
  ];

  return (
    <>
      {sharing.trim().toLowerCase() === "shared" ? (
        is_owner === true ? (
          <DupontButton
            type="tertiary"
            customClass="dupont-btn__tertiary--black  custom-sharing-button"
            onClick={(event) => {
              handleDialogOpen();
            }}
          >
            <u>{t("sharePlant.sharedWithOthers")}</u>
          </DupontButton>
        ) : (
          <span>{t("sharePlant.sharedByothers")}</span>
        )
      ) : (
        <span>{t("sharePlant.private")}</span>
      )}
      <DupontDialog
        open={dialogOpen}
        onClose={onClose}
        title={t("manageAccess.title")}
        confirmBtnText={t("ValueStrings.share")}
        cancelBtnText={t("ValueStrings.cancel")}
        minWidth={765}
        confirmDisabled={!email || errorMsg}
        isOverlayLoader={showOverlayLoader}
        customHeight={"620px"}
      >
        <div>
          <AgTable
            columnDefs={colDefs}
            rowData={rowData}
            hideExport
            customHeight={200}
            context={{
              deleteHandler,
            }}
          />
        </div>

        <div className="share marginTop10">
          {t("sharePlant.userMsg")} <span style={{ color: "red" }}>*</span>
        </div>
        <InputBox
          placeholder={t("sharePlant.enterEmail")}
          onChange={handleChange}
          maxLength={250}
        />

        <div
          style={{
            color: errorMsg ? "red" : "grey",
            textAlign: "left",
            marginTop: "10px",
          }}
        >
          {errorMsg || t("sharePlant.roUser")}
        </div>
        <div className="share marginTop10">{t("manageAccess.comments")}</div>
        <InputBox
          placeholder={t("manageAccess.comments")}
          rows={2}
          maxLength={225}
          onChange={handleCommentsChange}
        />
      </DupontDialog>
    </>
  );
};
